import React, {useState, useEffect, useMemo} from 'react'
import {useQuery, useMutation} from '@apollo/client'
import {Button, Offcanvas, Form, Nav, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {GET_BOTMAKER_WHATSAPP_TEMPLATES} from '../../gql/queries/botmaker'
import {SEND_BOTMAKER_WHATSAPP_TEMPLATE, SEND_BOTMAKER_TEXT_MESSAGE} from '../../gql/mutations/botmaker'
import {GET_TWILIO_WHATSAPP_TEMPLATES} from '../../gql/queries/twilio'
import {SEND_TWILIO_WHATSAPP_TEMPLATE} from '../../gql/mutations/twilio'
import {GET_MESSAGE_TEMPLATES} from '../../gql/queries/campaigns'
import {WhatsAppPreview} from '../../pages/templates/components/tiptap/WhatsAppPreview'
import {useGetUser} from '../../store/userStore'
import {PhoneInput} from '../PhoneInput'
import {useResolvedChannelConfig} from '../../pages/channelConfig/hooks/useChannelConfig'

interface SendMessageCanvasProps {
  show: boolean
  onHide: () => void
  recipient: {
    name: string
    phone: string
    email?: string
  }
  context?: {
    type: 'LEAD' | 'POLICY' | 'QUOTE' | 'CONTACT'
    id: string | number
  }
  data?: any // Context data (Lead, Policy, etc.) for auto-filling variables
  defaultChannel?: 'WHATSAPP' | 'SMS'
  onSuccess?: () => void
}

type WhatsAppTemplate = {
  id: string // normalized ID (botmaker_id or contentSid)
  name: string
  locale: string
  status?: string
  body?: any // can be string or object
  params?: string[]
  allowedModules?: string[]
  variableMapping?: any
  isActive?: boolean
  isArchived?: boolean
}

// ... existing helper functions ...

export const SendMessageCanvas: React.FC<SendMessageCanvasProps> = ({
  show,
  onHide,
  recipient,
  context,
  data, // Receive context data
  defaultChannel = 'WHATSAPP',
  onSuccess,
}) => {
  const currentUser = useGetUser((s) => s.user?.user)
  const {config: channelConfig, loading: loadingConfig} = useResolvedChannelConfig()

  // Resolve provider based on whatsapp_provider setting
  const provider = useMemo(() => {
    const configuredProvider = channelConfig?.whatsapp_provider

    // Use explicitly configured provider if set
    if (configuredProvider && configuredProvider !== 'NONE') {
      return configuredProvider
    }

    // Fallback: auto-detect from credentials if nothing is configured
    if (channelConfig?.botmaker_api_token && channelConfig?.botmaker_enabled !== false) return 'BOTMAKER'
    if (channelConfig?.twilio_account_sid && channelConfig?.twilio_enabled !== false) return 'TWILIO'
    if (channelConfig?.meta_whatsapp_access_token && channelConfig?.meta_whatsapp_phone_number_id) return 'META'

    return 'NONE'
  }, [channelConfig])

  // Log provider resolution for debugging
  useEffect(() => {
    console.log('WhatsApp Provider Resolution:', {
      configured: channelConfig?.whatsapp_provider,
      resolved: provider,
      botmakerEnabled: channelConfig?.botmaker_enabled,
      twilioEnabled: channelConfig?.twilio_enabled,
      hasBotmakerToken: !!channelConfig?.botmaker_api_token,
      hasTwilioSid: !!channelConfig?.twilio_account_sid,
    })
  }, [provider, channelConfig])
  
  // Helper to resolve dot notation path safely with case-insensitive support for snake/camel
  const resolvePath = (obj: any, path: string): any => {
      if (!obj || !path) return ''
      return path.split('.').reduce((acc, part) => {
          if (!acc) return undefined
          // 1. Try exact match
          if (acc[part] !== undefined) return acc[part]
          
          // 2. Try camelCase if part is snake_case
          const camelPart = part.replace(/_([a-z])/g, (g) => g[1].toUpperCase())
          if (camelPart !== part && acc[camelPart] !== undefined) return acc[camelPart]
          
          // 3. Try snake_case if part is camelCase
          const snakePart = part.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
          if (snakePart !== part && acc[snakePart] !== undefined) return acc[snakePart]
          
          return undefined
      }, obj) || ''
  }

  // ... state ...



  // Helper to get text from body (handling JSON string)
  const getBodyText = (template?: WhatsAppTemplate): string => {
      if (!template?.body) return ''
      try {
          const parsed = JSON.parse(template.body)
          return parsed.text || ''
      } catch (e) {
          console.warn('Failed to parse template body', e)
          return template.body || '' // Fallback if not JSON
      }
  }
  const [activeChannel, setActiveChannel] = useState<'WHATSAPP' | 'SMS'>(defaultChannel)
  
  // Ensure we switch to WHATSAPP if config loads and it's available, 
  // or switch to SMS if WHATSAPP is not configured.
  useEffect(() => {
    if (!loadingConfig && provider !== 'NONE') {
      setActiveChannel('WHATSAPP')
    } else if (!loadingConfig && provider === 'NONE') {
      setActiveChannel('SMS')
    }
  }, [loadingConfig, provider])
  
  // WhatsApp State
  const [selectedTemplateName, setSelectedTemplateName] = useState<string>('')
  const [templateParams, setTemplateParams] = useState<Record<string, string>>({})
  
  // SMS State
  const [smsText, setSmsText] = useState('')
  
  // WhatsApp Free Text (Session Message)
  const [waFreeText, setWaFreeText] = useState('')

  // Phone State (Allow manual correction)
  const [recipientPhone, setRecipientPhone] = useState(recipient.phone || '')

  // Queries & Mutations
  const {data: botmakerData, loading: loadingBotmaker} = useQuery(GET_BOTMAKER_WHATSAPP_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
    skip: !show || activeChannel !== 'WHATSAPP' || provider !== 'BOTMAKER',
  })

  const {data: twilioData, loading: loadingTwilio} = useQuery(GET_TWILIO_WHATSAPP_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
    skip: !show || activeChannel !== 'WHATSAPP' || provider !== 'TWILIO',
  })

  const loadingTemplates = loadingBotmaker || loadingTwilio || loadingConfig

  const {data: quickRepliesData} = useQuery(GET_MESSAGE_TEMPLATES, {
    // Fetch all to allow cross-channel quick replies or better filtering
    variables: { channel: null }, 
    fetchPolicy: 'cache-and-network',
    skip: !show
  })

  const [sendBotmakerWhatsApp, {loading: sendingBotmakerWA}] = useMutation(SEND_BOTMAKER_WHATSAPP_TEMPLATE)
  const [sendTwilioWhatsApp, {loading: sendingTwilioWA}] = useMutation(SEND_TWILIO_WHATSAPP_TEMPLATE)
  const [sendSMS, {loading: sendingSMS}] = useMutation(SEND_BOTMAKER_TEXT_MESSAGE)
  const [sendBotmakerFreeText, {loading: sendingBotmakerFT}] = useMutation(SEND_BOTMAKER_TEXT_MESSAGE)

  const sendingWA = sendingBotmakerWA || sendingTwilioWA || sendingBotmakerFT

  const templates = useMemo(() => {
    // Only load templates from the configured provider
    let rawList: any[] = []

    if (provider === 'BOTMAKER' && botmakerData?.botmakerWhatsappTemplates) {
      rawList = botmakerData.botmakerWhatsappTemplates.map((t: any) => ({
        id: t.id || t.name,
        name: t.name,
        locale: t.locale,
        status: t.status || t.state,
        body: t.body,
        allowedModules: t.allowedModules,
        variableMapping: t.variableMapping,
        isActive: t.isActive,
        isArchived: t.isArchived,
        provider: 'BOTMAKER'
      }))
    } else if (provider === 'TWILIO' && twilioData?.twilioWhatsappTemplates) {
      rawList = twilioData.twilioWhatsappTemplates.map((t: any) => ({
        id: t.contentSid,
        name: t.friendlyName,
        locale: t.language,
        status: t.approvalStatus?.toUpperCase(),
        body: t.body,
        allowedModules: t.allowedModules,
        variableMapping: t.variableMapping,
        isActive: t.isActive,
        isArchived: t.isArchived,
        provider: 'TWILIO'
      }))
    } else if (provider === 'META') {
      // TODO: Add META templates if needed
      rawList = []
    }

    console.log('SendMessageCanvas Templates Debug:', {
      configuredProvider: channelConfig?.whatsapp_provider,
      resolvedProvider: provider,
      rawListCount: rawList.length,
      activeChannel,
      context,
      rawListItems: rawList.map(t => ({
        name: t.name,
        status: t.status,
        isActive: t.isActive,
        isArchived: t.isArchived,
        provider: t.provider
      }))
    })

    let list = rawList as WhatsAppTemplate[]

    // Filter: inactive, archived, and approval status depends on provider
    list = list.filter(t => {
      const isNotArchived = t.isArchived !== true
      const isActive = t.isActive !== false

      // Different approval requirements by provider
      let isApprovalOk = true
      if (provider === 'BOTMAKER') {
        // Botmaker templates can have various statuses
        isApprovalOk = true
      } else if (provider === 'TWILIO') {
        // Twilio requires APPROVED status (WhatsApp Business requirement)
        isApprovalOk = (t.status || '').toUpperCase() === 'APPROVED'
      }

      return isNotArchived && isActive && isApprovalOk
    })
    
    // Filter by module/context
    if (context) {
         // simplistic plural mapping: LEAD -> leads, POLICY -> policies
         let module = ''
         if (context.type === 'LEAD') module = 'leads'
         if (context.type === 'POLICY') module = 'policies'
         // 'contracts'? 'CONTACT' -> contacts
         if (context.type === 'QUOTE') module = 'quotes' // not handled yet
         if (context.type === 'CONTACT') module = 'contacts'
         
         if (module) {
             list = list.filter(t => {
                 // If allowedModules is empty or null, allow everywhere? Or restrict?
                 // Typically restrict: if list exists, must match. If list empty, maybe global?
                 // Let's assume: empty = global/all. populated = restricted.
                 if (!t.allowedModules || t.allowedModules.length === 0) return true
                 if (module === 'contacts') {
                     // Contacts effectively support lead templates too
                     return t.allowedModules.includes('contacts') || t.allowedModules.includes('leads')
                 }
                 if (module === 'quotes') {
                     // Quotes use templates allowed for policies
                     return t.allowedModules.includes('quotes') || t.allowedModules.includes('policies')
                 }
                 return t.allowedModules.includes(module)
             })
         }
    }
    
    console.log('Final templates list:', list)
    return list
  }, [botmakerData, twilioData, context, provider])

  const quickReplies = useMemo(() => {
    return (quickRepliesData?.messageTemplates || [])
      .filter((t: any) => {
          if (!t.isActive) return false
          // If channel is specified, must match activeChannel
          if (t.channel && t.channel !== activeChannel) return false
          // If allChannels exists, activeChannel must be in it
          if (t.allChannels && t.allChannels.length > 0 && !t.allChannels.includes(activeChannel)) return false
          return true
      })
      .map((t: any) => ({
        id: t.id,
        name: t.name,
        body: t.body || (t.variants?.find((v: any) => v.isDefault)?.bodyText) || (t.variants?.[0]?.bodyText) || ''
      }))
  }, [quickRepliesData, activeChannel])

  useEffect(() => {
      if (quickRepliesData) {
          console.log('Quick Replies Data Loaded:', {
              count: quickRepliesData.messageTemplates?.length,
              filtered: quickReplies.length,
              activeChannel
          })
      }
  }, [quickRepliesData, quickReplies, activeChannel])

  const handleApplyQuickReply = (text: string) => {
    if (activeChannel === 'SMS') {
      setSmsText(prev => prev ? `${prev}\n${text}` : text)
    } else {
      setWaFreeText(prev => prev ? `${prev}\n${text}` : text)
    }
  }

  const defaultCountryCode = useMemo(() => {
    if (!data) return '1'
    const leadObj = data.lead || data;
    
    // Check personal lead country
    const personalCountry = leadObj.personalLead?.country?.toLowerCase() || ''
    if (personalCountry.includes('panama') || personalCountry.includes('panamá') || personalCountry === 'pa') return '507'
    if (personalCountry.includes('mexico') || personalCountry.includes('méxico') || personalCountry === 'mx') return '52'
    if (personalCountry.includes('colombia') || personalCountry === 'co') return '57'
    
    // Check campus name
    const campusName = leadObj.campus?.name?.toLowerCase() || ''
    if (campusName.includes('panama') || campusName.includes('panamá')) return '507'
    
    return '1'
  }, [data])

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedTemplateName)
  }, [templates, selectedTemplateName])

  const parsedMapping = useMemo(() => {
    if (!selectedTemplate?.variableMapping) return {}
    if (typeof selectedTemplate.variableMapping === 'string') {
        try {
             return JSON.parse(selectedTemplate.variableMapping)
        } catch(e) { return {} }
    }
    console.log('Selected Template Variable Mapping:', selectedTemplate.variableMapping)
    return selectedTemplate.variableMapping
  }, [selectedTemplate])

  // Auto-fill variables when template or data changes
  useEffect(() => {
      if (!selectedTemplate || !data || !context) return

      // Check module mapping (simple check, backend does robust check)
      let moduleKey = ''
      if (context.type === 'LEAD') moduleKey = 'leads'
      if (context.type === 'POLICY') moduleKey = 'policies'
      if (context.type === 'CONTACT') moduleKey = 'contacts'
      if (context.type === 'QUOTE') moduleKey = 'quotes'
      
      let mapping = parsedMapping[moduleKey]
      if (!mapping && moduleKey === 'contacts') mapping = parsedMapping['leads']
      if (!mapping && moduleKey === 'quotes') mapping = parsedMapping['policies']
      
      // Fallback to root mapping if still nothing and it looks like a flat mapping
      if (!mapping || typeof mapping !== 'object') mapping = parsedMapping

      if (!mapping || typeof mapping !== 'object' || Object.keys(mapping).length === 0) return

      const newParams: Record<string, string> = {}
      
      // Normalize data access: if data has 'quote' or 'policy' key, try to use it
      const contextData = data?.[context.type.toLowerCase()] || data;

      Object.entries(mapping).forEach(([paramName, path]) => {
          let val = ''
          const pStr = String(path)
          
          if (pStr.startsWith('agent.') && currentUser) {
             if (pStr === 'agent.name' || pStr === 'agent.full_name') val = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
             if (pStr === 'agent.email') val = currentUser.email || ''
             if (pStr === 'agent.first_name') val = currentUser.firstName || ''
             if (pStr === 'agent.last_name') val = currentUser.lastName || ''
          } else {
             // 1. Try exact path 
             val = resolvePath(contextData, pStr)
             
             // 2. If failed, try stripping module prefix (policy., quote., lead., contact.)
             if (!val && (pStr.startsWith('policy.') || pStr.startsWith('quote.') || pStr.startsWith('lead.') || pStr.startsWith('contact.'))) {
                 const stripped = pStr.split('.').slice(1).join('.')
                 val = resolvePath(contextData, stripped)
             }

             // 3. Special fallback for lead names if mapping expects them from .name
             if (!val && context.type === 'LEAD') {
                 if (pStr.includes('first_name')) val = (data.lead?.name || data.name || '').split(' ')[0] || ''
                 if (pStr.includes('last_name')) val = (data.lead?.name || data.name || '').split(' ').slice(1).join(' ') || ''
             }
          }
           
          if (val) newParams[paramName] = String(val)
      })
      
      if (Object.keys(newParams).length > 0) {
          setTemplateParams(prev => ({...prev, ...newParams}))
      }
  }, [selectedTemplate, parsedMapping, data, context, currentUser])

  // Reset state when opening
  useEffect(() => {
    if (show) {
      setActiveChannel(defaultChannel)
      setSmsText('')
      
      const names = (recipient.name || '').split(' ')
      const firstName = names[0] || ''
      const lastName = names.slice(1).join(' ') || ''
      const agentName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim()

      setTemplateParams({
          userName: agentName, 
          firstName: firstName,
          lastName: lastName,
          '1': recipient.name // Fallback
      })
      
      setRecipientPhone(recipient.phone || '')
    }
  }, [show, defaultChannel, recipient, currentUser])

  const handleSendWhatsApp = async () => {
    if (!selectedTemplateName) return
    if (!recipientPhone) {
        toast.error('Recipient phone number is required')
        return
    }
    
    const paramsList = Object.values(templateParams)
    const moduleMappings: Record<string, string> = {
      'LEAD': 'leads',
      'POLICY': 'policies',
      'QUOTE': 'quotes',
      'CONTACT': 'contacts'
    }
    const module = context ? moduleMappings[context.type] : undefined
    const objectId = context ? context.id.toString() : undefined

    console.log('Sending WhatsApp Payload:', {
        phone: recipientPhone,
        templateName: selectedTemplateName,
        language: selectedTemplate?.locale || 'es',
        params: paramsList,
        variables: templateParams, 
        module,
        objectId
    })

    try {
      if (provider === 'BOTMAKER') {
          if (selectedTemplateName) {
              await sendBotmakerWhatsApp({
                variables: {
                  phone: recipientPhone,
                  templateName: selectedTemplateName,
                  language: selectedTemplate?.locale || 'es',
                  params: paramsList,
                  variables: templateParams,
                  module,
                  objectId
                },
              })
          } else if (waFreeText) {
              await sendBotmakerFreeText({
                variables: {
                  phone: recipientPhone,
                  text: waFreeText,
                  module,
                  objectId
                }
              })
          } else {
              throw new Error('Please select a template or type a message')
          }
      } else if (provider === 'TWILIO') {
          if (!selectedTemplateName) throw new Error('Twilio requires a template to initiate conversation')
          await sendTwilioWhatsApp({
            variables: {
              phone: recipientPhone,
              contentSid: selectedTemplateName,
              variables: templateParams,
              module,
              objectId
            }
          })
      } else {
          throw new Error('No WhatsApp provider configured')
      }
      
      toast.success('WhatsApp sent successfully')
      if (onSuccess) onSuccess()
      onHide()
    } catch (err: any) {
      toast.error(err.message || 'Failed to send WhatsApp')
    }
  }

  const handleSendSMS = async () => {
    if (!smsText) return
    if (!recipientPhone) {
        toast.error('Recipient phone number is required')
        return
    }

    const moduleMappings: Record<string, string> = {
        'LEAD': 'leads',
        'POLICY': 'policies',
        'QUOTE': 'quotes',
        'CONTACT': 'contacts'
    }
    const module = context ? moduleMappings[context.type] : undefined
    const objectId = context ? context.id.toString() : undefined

    try {
        await sendSMS({
            variables: {
                phone: recipientPhone,
                text: smsText,
                module,
                objectId
            }
        })
        toast.success('SMS sent successfully')
        if (onSuccess) onSuccess()
        onHide()
    } catch (err: any) {
        toast.error(err.message || 'Failed to send SMS')
    }
  }

  // Helper to extract var names from body text like {{1}} or {{var}} or ${var}
  const extractVariables = (text?: string): string[] => {
    if (!text) return []
    // Match {{...}} or ${...}
    const regex = /\{\{([^}]+)\}\}|\$\{([^}]+)\}/g
    const vars = new Set<string>()
    let match
    while ((match = regex.exec(text)) !== null) {
      vars.add(match[1] || match[2])
    }
    return Array.from(vars)
  }

  const currentTemplateVars = useMemo(() => {
    return extractVariables(getBodyText(selectedTemplate))
  }, [selectedTemplate])

  const previewMessage = useMemo(() => {
    let msg = getBodyText(selectedTemplate)
    if (!msg) return ''
    
    currentTemplateVars.forEach(v => {
        const val = templateParams[v]
        if (val) {
            // Replace both {{v}} and ${v}
            // Escape v for regex just in case
            const escapedV = v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            msg = msg.replace(new RegExp(`\\{\\{${escapedV}\\}\\}`, 'g'), val)
            msg = msg.replace(new RegExp(`\\$\\{${escapedV}\\}`, 'g'), val)
        }
    })
    
    return msg
  }, [selectedTemplate, currentTemplateVars, templateParams])

  // Debug Effect
  useEffect(() => {
    if (selectedTemplate) {
        const text = getBodyText(selectedTemplate)
        console.group('🔴 Template Preview Debug')
        console.log('Type:', 'WHATSAPP')
        console.log('Template Name:', selectedTemplate.name)
        console.log('Raw Body (Parsed):', text)
        console.log('Current Params:', templateParams)
        console.log('Rendered Preview:', previewMessage)
        console.groupEnd()
    }
  }, [selectedTemplate, templateParams, previewMessage])

  return (
    <Offcanvas show={show} onHide={onHide} placement='end' className='w-100 w-md-75 w-lg-75'>
      <Offcanvas.Header closeButton className='bg-light'>
        <Offcanvas.Title>
            <i className='bi bi-chat-dots-fill me-2 text-primary'></i>
            Send Message to {recipient.name}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className='d-flex flex-column'>
        <Nav variant='tabs' className='mb-3' activeKey={activeChannel} onSelect={(k) => setActiveChannel(k as any)}>
            <Nav.Item>
                <Nav.Link eventKey='WHATSAPP'>
                    <i className='bi bi-whatsapp success me-1'></i> WhatsApp
                </Nav.Link>
            </Nav.Item>
            <Nav.Item>
                <Nav.Link eventKey='SMS'>
                    <i className='bi bi-chat-square-text me-1'></i> SMS
                </Nav.Link>
            </Nav.Item>
        </Nav>

        <div className='bg-light p-3 rounded mb-3'>
            <PhoneInput
                id='recipient-phone'
                name='phone'
                label='Recipient Phone'
                value={recipientPhone}
                defaultCountryCode={defaultCountryCode}
                onChange={(_, val) => setRecipientPhone(val)}
                placeholder='Enter phone number'
            />
            <div className='small text-muted mt-1'>Format: International digits including country code (e.g. 50768591251)</div>
        </div>

        {activeChannel === 'WHATSAPP' && (
            <div className='flex-grow-1 d-flex flex-column'>
                {provider === 'NONE' ? (
                    <div className='alert alert-warning'>
                        <i className='bi bi-exclamation-triangle me-2'></i>
                        <strong>WhatsApp not configured</strong>
                        <p className='mb-0 mt-2 small'>
                          No WhatsApp provider (Botmaker, Twilio, or Meta) has been enabled for your organization or user account.
                          Please configure one in Settings → Channel Configuration.
                        </p>
                    </div>
                ) : loadingTemplates ? (
                    <div className='text-center py-5'>
                        <Spinner animation='border' variant='primary' />
                        <div className='mt-2 small text-muted'>Loading {provider} templates...</div>
                    </div>
                ) : (
                    <>
                        {templates.length === 0 ? (
                          <div className='alert alert-info'>
                            <i className='bi bi-info-circle me-2'></i>
                            <strong>{provider} Templates</strong>
                            <p className='mb-0 mt-2 small'>
                              No active templates found for {provider}.
                              {provider === 'TWILIO' && ' Make sure your templates are APPROVED in Twilio and marked as Active in PolicyTools.'}
                              {provider === 'BOTMAKER' && ' Create or enable templates in your Botmaker workspace.'}
                            </p>
                          </div>
                        ) : (
                          <Form.Group className='mb-3'>
                            <Form.Label>Select Template ({provider})</Form.Label>
                            <Form.Select
                              value={selectedTemplateName}
                              onChange={(e) => {
                                const newVal = e.target.value
                                setSelectedTemplateName(newVal)
                                setTemplateParams({})
                                if (newVal) setWaFreeText('') // Clear free text if template selected
                              }}
                            >
                              <option value=''>-- Choose a template --</option>
                              {templates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name} ({t.locale})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        )}

                        {provider === 'BOTMAKER' && !selectedTemplateName && (
                            <Form.Group className='mb-3'>
                                <Form.Label className='text-muted small text-uppercase fw-bold'>O: Texto Libre (Solo Sesión)</Form.Label>
                                <Form.Control 
                                    as='textarea' 
                                    rows={3}
                                    placeholder='Escribe un mensaje si hay una sesión activa...'
                                    value={waFreeText}
                                    onChange={(e) => setWaFreeText(e.target.value)}
                                />
                            </Form.Group>
                        )}

                        {quickReplies.length > 0 && (
                            <div className='mb-4 bg-light-primary p-3 rounded border border-primary border-dashed'>
                                <label className='form-label fw-bold small text-primary text-uppercase mb-2'>
                                    <i className='bi bi-lightning-fill me-1'></i> Respuestas Rápidas
                                </label>
                                <div className='d-flex flex-wrap gap-2 max-h-150px overflow-auto p-1'>
                                    {quickReplies.map((qr) => (
                                        <button
                                            key={qr.id}
                                            type='button'
                                            className='btn btn-sm btn-white border border-primary text-primary px-3 shadow-sm hover-elevate-up'
                                            onClick={() => handleApplyQuickReply(qr.body)}
                                            title={qr.body}
                                        >
                                            {qr.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedTemplate && (
                            <div className='flex-grow-1 overflow-auto mb-3'>
                                <div className='mb-3'>
                                    <WhatsAppPreview 
                                        message={previewMessage}
                                        // TODO: Pass header/footer if available in template object
                                    />
                                </div>

                                {currentTemplateVars.length > 0 && (
                                    <div className='bg-light p-3 rounded'>
                                        <h6 className='fw-bold mb-2'>Variables</h6>
                                        {currentTemplateVars.map((v) => {
                                            // Determine current module key
                                            let modKey = ''
                                            if (context?.type === 'LEAD') modKey = 'leads'
                                            else if (context?.type === 'POLICY') modKey = 'policies'
                                            else if (context?.type === 'CONTACT') modKey = 'contacts'
                                            
                                            // Check mapping: support both flat (legacy) and nested
                                            const mappingRoot = parsedMapping
                                            let mappedLabel = mappingRoot[v] // First check root (legacy)
                                                                                        // Check module specific
                                             if (modKey && mappingRoot[modKey] && typeof mappingRoot[modKey] === 'object') {
                                                  const modMap = mappingRoot[modKey]
                                                  if (modMap[v]) mappedLabel = modMap[v]
                                             }
                                             
                                             // Fallback for contacts -> leads
                                             if (!mappedLabel && modKey === 'contacts' && mappingRoot['leads']) {
                                                 mappedLabel = mappingRoot['leads'][v]
                                             }

                                            return (
                                            <Form.Group key={v} className='mb-2'>
                                                <div className='d-flex justify-content-between align-items-center mb-1'>
                                                    <Form.Label className='small text-muted mb-0'>{v}</Form.Label>
                                                    {mappedLabel && <span className='badge bg-success text-dark x-small'>Detected: {mappedLabel}</span>}
                                                </div>
                                                
                                                <Form.Control 
                                                    size='sm'
                                                    value={templateParams[v] || ''}
                                                    onChange={(e) => setTemplateParams({...templateParams, [v]: e.target.value})}
                                                    placeholder={`Value for ${v}`}
                                                />
                                                {mappedLabel && (
                                                    <div className='small text-muted fst-italic'>
                                                        Mapped to <strong>{mappedLabel}</strong>.
                                                    </div>
                                                )}
                                            </Form.Group>
                                        )})}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
                <div className='mt-auto pt-3 border-top d-flex justify-content-end gap-2'>
                    <Button variant='secondary' onClick={onHide}>Cancel</Button>
                    <Button 
                        variant='success' 
                        onClick={handleSendWhatsApp}
                        disabled={(!selectedTemplateName && !waFreeText) || sendingWA}
                    >
                        {sendingWA ? <Spinner size='sm' animation='border' className='me-1'/> : <i className='bi bi-whatsapp me-1'></i>}
                        Send WhatsApp
                    </Button>
                </div>
            </div>
        )}

        {activeChannel === 'SMS' && (
             <div className='flex-grow-1 d-flex flex-column'>
                <div className='alert alert-info'>
                    <i className='bi bi-info-circle me-2'></i>
                    SMS functionality is basic.
                </div>
                <Form.Group className='flex-grow-1'>
                    <Form.Control 
                        as='textarea' 
                        className='h-100'
                        placeholder='Type your SMS message here...'
                        value={smsText}
                        onChange={(e) => setSmsText(e.target.value)}
                    />
                </Form.Group>

                {quickReplies.length > 0 && (
                    <div className='mt-3 bg-light-primary p-3 rounded border border-primary border-dashed'>
                        <label className='form-label fw-bold small text-primary text-uppercase mb-2'>
                            <i className='bi bi-lightning-fill me-1'></i> Respuestas Rápidas
                        </label>
                        <div className='d-flex flex-wrap gap-2 max-h-150px overflow-auto p-1'>
                            {quickReplies.map((qr) => (
                                <button
                                    key={qr.id}
                                    type='button'
                                    className='btn btn-sm btn-white border border-primary text-primary px-3 shadow-sm hover-elevate-up'
                                    onClick={() => handleApplyQuickReply(qr.body)}
                                    title={qr.body}
                                >
                                    {qr.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className='mt-3 pt-3 border-top d-flex justify-content-end gap-2'>
                    <Button variant='secondary' onClick={onHide}>Cancel</Button>
                    <Button variant='primary' onClick={handleSendSMS} disabled={!smsText || sendingSMS}>
                        {sendingSMS ? <Spinner size='sm' animation='border' className='me-1'/> : <i className='bi bi-chat-left-dots me-1'></i>}
                        Send SMS
                    </Button>
                </div>
             </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}
